import React from "react";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { colors } from "src/theme/colors";
import { seriesTone } from "src/theme/ui";

/**
 * A run of values, small enough to sit in a table row.
 *
 * The product's own, rather than the charting library's TinyArea. That one
 * draws to a canvas and reserves a gutter of its own, so a 132px spark line
 * was drawn in about eighty of them; its colours had to be spelled out as
 * gradient strings, which is how one of them ended up with a stop index
 * where a colour belongs and a wash that never appeared; and it smoothed
 * between readings, which on a price series draws values that were never
 * recorded.
 *
 * A line and nothing else — no wash under it and no marker at its end. The
 * fill needed height to read as area and at this size only made the line
 * look like it was sinking into something; the end point was a dot on a
 * drawing whose whole job is the shape of the run, and it read as a piece of
 * punctuation the line did not need. Rounded joins, one colour, one stroke.
 */

type Props = {
  values: number[];
  width?: number;
  height?: number;
};

/**
 * What period a spark series actually covers, in words.
 *
 * NOT from the field it arrived in. The gateway sends this series as
 * `spark_line_12_month` and it holds five weekly readings — 7 Aug to 7 Sep
 * when this was written, one month — so a card that captions it from the
 * field name is telling the reader something the numbers under it deny.
 * That caption has now been wrong in both directions: it said "Last Month"
 * over a series nobody had checked, was "corrected" to 12 months on the
 * strength of the field name, and the data says the first one was right.
 *
 * So it is read off the keys, which are dates. A series whose span changes
 * relabels itself, and a series with no usable dates says nothing at all
 * rather than guessing.
 */
export const sparkSpan = (series?: Record<string, number> | null) => {
  const days = Object.keys(series ?? {})
    .map((key) => Date.parse(key.replace(" ", "T")))
    .filter((time) => Number.isFinite(time))
    .sort((a, b) => a - b);
  if (days.length < 2) return null;

  const span = (days[days.length - 1] - days[0]) / 86_400_000;
  const months = Math.round(span / 30.44);
  if (months <= 1) return "Last month";
  if (months >= 12)
    return `Last ${Math.round(months / 12)} year`.replace(
      "Last 1 year",
      "Last year",
    );
  return `Last ${months} months`;
};

const Sparkline: React.FC<Props> = ({ values, width = 108, height = 34 }) => {
  const data = React.useMemo(
    () => values.map((value, index) => ({ index, value })),
    [values],
  );

  if (data.length < 2) return <div style={{ width, height }} />;

  /* Where it ENDED against where it began — the same rule the tone helpers
     use everywhere else, so a rising spark is the green a gain is and a
     falling one the red a loss is. */
  const rose = data[data.length - 1].value >= data[0].value;
  /* The brighter step, not the text one. A chip has to be READ, so it takes
     the deep 700 for contrast against its tint; a line is a graphic and
     wants the saturation — at 700 the falling case came out a dark brown. */
  /* The same pair the page-sized chart draws in, so a row of sparklines and
     the chart they open onto are the same two colours. */
  const stroke = rose ? seriesTone.up : seriesTone.down;

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        {/* Room at the top and bottom only: the line runs edge to edge, and
            the inset is what keeps a peak and the marked end point from
            being clipped by the frame. */}
        <LineChart
          data={data}
          /* Only what the stroke and the end point need. With the fill gone
             there is nothing to keep clear of the frame but the line itself,
             and every pixel given back is shape the reader can see. */
          margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
        >
          {/* Hidden, but declared. Left to itself the chart builds a
              CATEGORY axis from the data, and a marker placed at a numeric
              index against a category scale lands wherever it happens to
              land — usually nowhere. Declared as a number line, the end
              point is exactly where the line ends.

              And the value axis spans the data rather than reaching to zero,
              so a series that moved two per cent over the year still fills
              the box: a spark line is about shape, and a shape flattened
              against a zero baseline has none. */}
          <XAxis
            dataKey="index"
            type="number"
            domain={[0, data.length - 1]}
            hide
          />
          <YAxis type="number" domain={["dataMin", "dataMax"]} hide />
          <Line
            /* Curved, unlike the page-sized chart. That one is read against
               an axis with a tooltip on it, so a curve bending between two
               readings would show the holding at levels it never traded at.
               A spark has no axis and no tooltip: nobody takes a value off
               it, they take a SHAPE, and a curve carries a shape better than
               a run of hinges. Monotone rather than a natural spline — it
               bends between points without overshooting them, so a curve
               never invents a peak above the highest reading. */
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Sparkline;
